/**
 * Settings ▸ Groups — named member sets carrying Space-role bindings
 * (workspace-organization groups-model.md).
 *
 * A group is an assignment convenience, not a new access path: members
 * inherit the group's (space, Space-role) bindings through the same grants
 * pipeline as their own. Bindings are capped server-side to the grantor's own
 * per-space access (no owner break-glass on the group path), and guests can
 * never join a group.
 *
 * Admin-gated: non-admins see the list read-only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Users2,
  X,
} from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { useTenant } from '@/contexts/TenantContext';
import {
  problemDetail,
  tenantApi,
  type Preset,
  type TenantBinding,
  type TenantBindingView,
  type TenantGroup,
  type TenantMember,
} from '@/services/tenantApi';
import { spacesApi, type Space } from '@/services/spacesApi';
import {
  bindingPreset,
  bindingSlug,
  presetBinding,
  presetLabel,
  toWriteBinding,
} from '@/lib/grants';
import { usePresets } from '@/lib/usePresets';

const selectClass =
  'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-700 dark:text-slate-300 focus:border-purple-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';

const inputClass =
  'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none';

/**
 * Strip the optional `usr_` prefix so ids compare equal regardless of which
 * service produced them: the group-membership API and `listMembers` don't
 * always agree on the prefix, and an exact match would otherwise fall through
 * to rendering the raw id.
 */
function bareUserId(id: string): string {
  return id.startsWith('usr_') ? id.slice(4) : id;
}

/** Avatar letter for a member card — first char of name, then email. */
function memberInitial(member: TenantMember | undefined): string {
  return (member?.name || member?.email || '?').charAt(0).toUpperCase();
}

/**
 * What one binding grants, as a word: the catalogue preset it amounts to
 * ("Developer"); else the raw slug the row itself carries — a legacy persona
 * the catalogue no longer knows is still the truest name it has; else its
 * scope count, because custom access has no name and inventing one would
 * misreport the grant.
 */
function bindingLabel(b: TenantBindingView, presets: readonly Preset[]): string {
  const slug = bindingPreset(b, presets);
  if (slug) return presetLabel(slug, presets);
  const own = bindingSlug(b);
  if (own) return own;
  const n = b.scopes?.length ?? 0;
  return n === 0 ? 'Custom (no scopes)' : `Custom (${n} scope${n === 1 ? '' : 's'})`;
}

/** Human summary of a group's bindings, e.g. "Architect in Payments · +1". */
export function bindingsSummary(
  bindings: TenantBindingView[],
  spaceName: (id: string) => string,
  presets: readonly Preset[],
): string {
  if (bindings.length === 0) return 'No access yet';
  const [first] = bindings;
  const label = bindingLabel(first, presets);
  const where = first.space === '*' ? 'all open spaces' : spaceName(first.space);
  const rest = bindings.length - 1;
  return rest > 0 ? `${label} in ${where} · +${rest} more` : `${label} in ${where}`;
}

export default function GroupsTab() {
  const { tenant } = useTenant();
  const tenantId = tenant?.tenantId ?? '';
  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';

  const [groups, setGroups] = useState<TenantGroup[]>([]);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [groupMembers, setGroupMembers] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Bindings editor state for the expanded group, in the WRITE shape.
  const [editBindings, setEditBindings] = useState<TenantBinding[] | null>(null);

  // The catalogue behind the preset pickers. Null while loading or after a
  // failed read — the editor cannot open without it (seeding needs the
  // scopes) and `presetsError` is shown; there is no built-in list to fall
  // back to. Labels meanwhile degrade to the row's own slug or scope count.
  const { presets, error: presetsError } = usePresets();
  const presetOf = (b: TenantBindingView) => bindingPreset(b, presets ?? []);

  const spaceName = useCallback(
    (id: string) => spaces.find((s) => s.spaceId === id)?.name ?? id,
    [spaces],
  );
  const resolveMember = useCallback(
    (userId: string) => members.find((m) => bareUserId(m.userId) === bareUserId(userId)),
    [members],
  );

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [groupList, memberList] = await Promise.all([
        tenantApi.listGroups(tenantId),
        tenantApi.listMembers(tenantId),
      ]);
      setGroups(groupList);
      setMembers(memberList);
      setError(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not load groups.'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    spacesApi
      .listSpaces()
      .then((all) => setSpaces(all.filter((s) => !s.archivedAt)))
      .catch(() => setSpaces([]));
  }, [tenantId]);

  const toggleExpand = async (group: TenantGroup) => {
    setEditBindings(null);
    if (expanded === group.groupId) {
      setExpanded(null);
      return;
    }
    setExpanded(group.groupId);
    try {
      const detail = await tenantApi.getGroup(tenantId, group.groupId);
      setGroupMembers((prev) => ({ ...prev, [group.groupId]: detail.members }));
    } catch (err) {
      setError(problemDetail(err, 'Could not load the group.'));
    }
  };

  const createGroup = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await tenantApi.createGroup(tenantId, {
        name: newName.trim(),
        description: newDescription.trim(),
      });
      setGroups((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setNewName('');
      setNewDescription('');
    } catch (err) {
      setError(problemDetail(err, 'Could not create the group.'));
    } finally {
      setBusy(false);
    }
  };

  const deleteGroup = async (groupId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await tenantApi.deleteGroup(tenantId, groupId);
      setGroups((prev) => prev.filter((g) => g.groupId !== groupId));
      setConfirmDelete(null);
      if (expanded === groupId) setExpanded(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not delete the group.'));
    } finally {
      setBusy(false);
    }
  };

  const saveBindings = async (groupId: string) => {
    if (!editBindings || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await tenantApi.setGroupBindings(tenantId, groupId, editBindings);
      setGroups((prev) => prev.map((g) => (g.groupId === groupId ? { ...g, ...updated } : g)));
      setEditBindings(null);
    } catch (err) {
      // binding_exceeds_grantor 403 names the scopes the caller lacks in that space.
      setError(problemDetail(err, 'Could not save the group’s access.'));
    } finally {
      setBusy(false);
    }
  };

  const addMember = async (groupId: string, userId: string) => {
    if (!userId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await tenantApi.addGroupMember(tenantId, groupId, userId);
      setGroupMembers((prev) => ({
        ...prev,
        [groupId]: [...(prev[groupId] ?? []), userId].sort(),
      }));
      setGroups((prev) =>
        prev.map((g) =>
          g.groupId === groupId ? { ...g, memberCount: g.memberCount + 1 } : g,
        ),
      );
    } catch (err) {
      setError(problemDetail(err, 'Could not add the member.'));
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (groupId: string, userId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await tenantApi.removeGroupMember(tenantId, groupId, userId);
      setGroupMembers((prev) => ({
        ...prev,
        [groupId]: (prev[groupId] ?? []).filter((u) => u !== userId),
      }));
      setGroups((prev) =>
        prev.map((g) =>
          g.groupId === groupId ? { ...g, memberCount: Math.max(0, g.memberCount - 1) } : g,
        ),
      );
    } catch (err) {
      setError(problemDetail(err, 'Could not remove the member.'));
    } finally {
      setBusy(false);
    }
  };

  // Members eligible to join a group: internal members not already in it.
  // Match on the bare id so a prefix mismatch doesn't offer someone twice.
  const eligibleFor = useMemo(
    () => (groupId: string) => {
      const present = new Set((groupMembers[groupId] ?? []).map(bareUserId));
      return members.filter(
        (m) => m.memberClass !== 'guest' && !present.has(bareUserId(m.userId)),
      );
    },
    [members, groupMembers],
  );

  if (loading) {
    return (
      <LoadingState size="lg" />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Users2 className="w-5 h-5 text-slate-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Groups</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Named sets of members. Assign a Space role to the group once — everyone in it
            inherits the access.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}
      {presetsError && (
        <div className="mb-4 px-4 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Access presets unavailable — {presetsError}
        </div>
      )}

      {canManage && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <input
            className={inputClass}
            placeholder="Group name (e.g. Payments Architects)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={100}
          />
          <input
            className={`${inputClass} flex-1 min-w-48`}
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            maxLength={500}
          />
          <button
            type="button"
            onClick={createGroup}
            disabled={busy || !newName.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Create group
          </button>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          No groups yet.{' '}
          {canManage
            ? 'Create one to grant a whole team access in one step.'
            : 'Workspace admins can create groups here.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {groups.map((group) => {
            const open = expanded === group.groupId;
            // The write shape is a valid view, so one list serves both modes.
            const bindings: TenantBindingView[] = editBindings ?? group.bindings;
            return (
              <li
                key={group.groupId}
                className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(group)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                >
                  {open ? (
                    <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  )}
                  <span className="font-medium text-sm text-slate-900 dark:text-slate-100">
                    {group.name}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {bindingsSummary(group.bindings, spaceName, presets ?? [])}
                  </span>
                  <span className="ml-auto text-xs text-slate-400 shrink-0">
                    {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
                  </span>
                </button>

                {open && (
                  <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-4 space-y-5">
                    {group.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {group.description}
                      </p>
                    )}

                    {/* Space-role bindings */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                        Access (Space roles)
                      </h3>
                      <div className="space-y-1.5">
                        {bindings.map((b, i) => (
                          <div key={`${b.space}-${i}`} className="flex items-center gap-2">
                            {canManage && editBindings ? (
                              <>
                                <select
                                  className={selectClass}
                                  value={b.space}
                                  onChange={(e) =>
                                    setEditBindings((prev) =>
                                      (prev ?? []).map((x, j) =>
                                        j === i ? { ...x, space: e.target.value } : x,
                                      ),
                                    )
                                  }
                                >
                                  <option value="*">All open spaces</option>
                                  {spaces.map((s) => (
                                    <option key={s.spaceId} value={s.spaceId}>
                                      {s.name}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={selectClass}
                                  value={presetOf(b) ?? 'custom'}
                                  title="Space role — what group members can do with content"
                                  disabled={!presets}
                                  onChange={(e) => {
                                    // The preset's scopes travel with its label:
                                    // the server stores the scopes and refuses a
                                    // label that disagrees with them.
                                    const entry = presets?.find((p) => p.slug === e.target.value);
                                    if (!entry) return;
                                    setEditBindings((prev) =>
                                      (prev ?? []).map((x, j) =>
                                        j === i
                                          ? { ...x, preset: entry.slug, scopes: [...entry.scopes] }
                                          : x,
                                      ),
                                    );
                                  }}
                                >
                                  {presetOf(b) === undefined && (
                                    // Scopes no preset names: kept as-is on save,
                                    // replaced only by an explicit pick.
                                    <option value="custom" disabled>
                                      {bindingLabel(b, presets ?? [])}
                                    </option>
                                  )}
                                  {(presets ?? []).map((p) => (
                                    <option key={p.slug} value={p.slug} title={p.description}>
                                      {p.label}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditBindings((prev) =>
                                      (prev ?? []).filter((_, j) => j !== i),
                                    )
                                  }
                                  className="p-1 text-slate-400 hover:text-red-600"
                                  title="Remove this access"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <span className="text-sm text-slate-700 dark:text-slate-300">
                                {bindingLabel(b, presets ?? [])}{' '}
                                in{' '}
                                <strong>
                                  {b.space === '*' ? 'all open spaces' : spaceName(b.space)}
                                </strong>
                              </span>
                            )}
                          </div>
                        ))}
                        {bindings.length === 0 && (
                          <p className="text-sm text-slate-400">
                            This group grants no access yet.
                          </p>
                        )}
                      </div>
                      {canManage && (
                        <div className="mt-2 flex items-center gap-2">
                          {editBindings ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!presets) return;
                                  try {
                                    const added = presetBinding(
                                      spaces[0]?.spaceId ?? '*',
                                      'developer',
                                      presets,
                                    );
                                    setEditBindings((prev) => [...(prev ?? []), added]);
                                  } catch (err) {
                                    setError(err instanceof Error ? err.message : String(err));
                                  }
                                }}
                                disabled={!presets}
                                className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                              >
                                + Add access
                              </button>
                              <button
                                type="button"
                                onClick={() => saveBindings(group.groupId)}
                                disabled={busy}
                                className="px-2.5 py-1 rounded-md bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                              >
                                Save access
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditBindings(null)}
                                className="text-xs text-slate-400 hover:text-slate-600"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                if (!presets) return;
                                try {
                                  // Seed the editor with what is stored, in the
                                  // write shape; a row it cannot express refuses
                                  // here, before anything can be saved.
                                  setEditBindings(
                                    group.bindings.map((b) => toWriteBinding(b, presets)),
                                  );
                                } catch (err) {
                                  setError(err instanceof Error ? err.message : String(err));
                                }
                              }}
                              disabled={!presets}
                              className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline"
                            >
                              Edit access
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Members */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                        Members
                      </h3>
                      <ul className="space-y-1.5">
                        {(groupMembers[group.groupId] ?? []).map((userId) => {
                          const m = resolveMember(userId);
                          return (
                            <li key={userId} className="flex items-center gap-2.5">
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50 text-xs font-semibold text-purple-700 dark:text-purple-300">
                                {memberInitial(m)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                                  {m?.name || m?.email || 'Unknown member'}
                                </div>
                                {m?.name && m?.email ? (
                                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                    {m.email}
                                  </div>
                                ) : !m ? (
                                  // Still in the group but no longer a workspace member —
                                  // keep a muted id so an admin can prune the ghost.
                                  <div className="truncate text-xs text-slate-400 dark:text-slate-500">
                                    {userId}
                                  </div>
                                ) : null}
                              </div>
                              {canManage && (
                                <button
                                  type="button"
                                  onClick={() => removeMember(group.groupId, userId)}
                                  disabled={busy}
                                  className="p-0.5 text-slate-400 hover:text-red-600"
                                  title="Remove from group"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                        {(groupMembers[group.groupId] ?? []).length === 0 && (
                          <li className="text-sm text-slate-400">No members yet.</li>
                        )}
                      </ul>
                      {canManage && eligibleFor(group.groupId).length > 0 && (
                        <select
                          className={`${selectClass} mt-2`}
                          value=""
                          disabled={busy}
                          title="Guests cannot join groups"
                          onChange={(e) => addMember(group.groupId, e.target.value)}
                        >
                          <option value="" disabled>
                            Add a member…
                          </option>
                          {eligibleFor(group.groupId).map((m) => (
                            <option key={m.userId} value={m.userId}>
                              {m.name && m.email
                                ? `${m.name} (${m.email})`
                                : m.name || m.email || m.userId}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Danger */}
                    {canManage && (
                      <div className="pt-1">
                        {confirmDelete === group.groupId ? (
                          <span className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => deleteGroup(group.groupId)}
                              disabled={busy}
                              className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              Delete group
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(null)}
                              className="text-xs text-slate-400 hover:text-slate-600"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(group.groupId)}
                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete group
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
